import dayjs from "dayjs";
import { Check, ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** Clave de mes tal como la devuelve el backend. */
export type MonthKey = string;

export function currentMonthKey(): MonthKey {
  return dayjs().format("YYYY-MM");
}

/** "Septiembre", "Septiembre 2025" cuando no es el año en curso. */
export function monthLabel(month: MonthKey): string {
  const date = dayjs(`${month}-01`);
  const name = date.format("MMMM");
  const capitalized = name.charAt(0).toUpperCase() + name.slice(1);
  return date.year() === dayjs().year()
    ? capitalized
    : `${capitalized} ${date.year()}`;
}

/** Mes anterior a uno dado, para la comparación. */
export function previousMonth(month: MonthKey): MonthKey {
  return dayjs(`${month}-01`).subtract(1, "month").format("YYYY-MM");
}

/**
 * El mes del que se habla. Va en el título porque es lo que ordena toda la banda
 * de métricas: cuánto entró, cuánto se cerró y contra qué mes se compara.
 *
 * Las opciones salen de los meses que el backend devolvió, no de un calendario
 * abierto: ofrecer 2019 cuando la cuenta se creó en marzo es prometer datos que
 * no existen.
 */
export function MonthPicker({
  value,
  months,
  onChange,
}: {
  value: MonthKey;
  months: MonthKey[];
  onChange: (month: MonthKey) => void;
}) {
  const options = [...months].reverse();
  const current = currentMonthKey();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-0.5 text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        {monthLabel(value)}
        <ChevronDown className="size-5 opacity-50 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        {options.map((month) => (
          <DropdownMenuItem
            key={month}
            onSelect={() => onChange(month)}
            className="cursor-pointer gap-2"
          >
            <span className="flex-1">{monthLabel(month)}</span>
            {month === current && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                en curso
              </span>
            )}
            {month === value && <Check className="size-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Tres barras con la altura proporcional al valor. No es un gráfico: es la forma
 * de la tendencia al lado del número, para saber si el salto de este mes es una
 * racha o un rebote. El mes en curso va en color; los anteriores, apagados.
 */
export function Sparkline({
  values,
  months,
  label,
  className,
}: {
  values: number[];
  /** Solo para el tooltip: qué mes es cada barra. */
  months?: string[];
  label?: string;
  className?: string;
}) {
  const max = Math.max(...values, 1);

  return (
    <span className={cn("flex h-5 items-end gap-1", className)}>
      {values.map((value, index) => {
        const isLast = index === values.length - 1;
        // Un cero también se dibuja: una barra ausente se lee como "sin dato".
        const height = Math.max((value / max) * 100, 14);
        return (
          <span
            key={index}
            title={
              months?.[index]
                ? `${monthLabel(months[index]!)}: ${value}${label ? ` ${label}` : ""}`
                : String(value)
            }
            style={{ height: `${height}%` }}
            className={cn(
              "w-2 rounded-[3px]",
              isLast
                ? "bg-primary"
                : "bg-slate-200 dark:bg-slate-700",
            )}
          />
        );
      })}
    </span>
  );
}
